import { useId } from 'react';
import { Tag } from 'sonata-react-components';

import { getBuyerInsightsFigure } from '@/helpers/common/buyerInsightsFigure.helper';
import { BrandSection } from './BrandSection/BrandSection';
import { DemographicItem } from './DemographicItem/DemographicItem';
import { getPoiCategoryIcon } from './poiCategoryIcon.helpers';

import './audienceInsights.scss';

export type AudienceInsightsBrand = {
  logoS3: string | null;
  name: string;
};

/**
 * Profile shape produced by the audience profiling AI agents. Both the
 * driveToStore buyer insights and the DOOH audience profiling payloads satisfy
 * it, which is what lets the two reports share this presentation.
 */
export type AudienceInsightsData = {
  age: string;
  /** Low-affinity brands; only in profiles generated before the agent returned `top_categories`. */
  bottom_brands?: AudienceInsightsBrand[];
  core_needs: string;
  gender: 'women' | 'men';
  iab_cat: string[];
  person_target: string;
  primary_values: string[];
  status: string;
  top_brands: AudienceInsightsBrand[];
  /** POI categories with the highest affinity, highest first. Missing in older profiles. */
  top_categories?: string[];
};

/**
 * All copy is injected so each report keeps its own i18n namespace — this
 * component holds no messages of its own.
 */
export type AudienceInsightsLabels = {
  affinitiesHeader: string;
  avgAge: string;
  bottomBrands: string;
  coreNeeds: string;
  genderLabel: string;
  header: string;
  highAffinity: string;
  interests: string;
  men: string;
  primaryValues: string;
  statusLabel: string;
  subHeader: string;
  targetPersona: string;
  topBrands: string;
  topCategories: string;
  women: string;
  yearsOld: string;
};

type Props = {
  data: AudienceInsightsData;
  labels: AudienceInsightsLabels;
};

export function AudienceInsights({ data, labels }: Readonly<Props>) {
  const tagIdPrefix = useId();

  const {
    gender,
    status: visitorType,
    age,
    iab_cat,
    core_needs,
    person_target,
    primary_values,
    top_brands,
    top_categories,
    bottom_brands
  } = data;

  const genderLabel = gender === 'women' ? labels.women : labels.men;
  const [ageFrom, ageTo] = age.split('-');

  return (
    <div className="audienceInsights">
      <div className="audienceInsights__insightsContent">
        <div className="audienceInsights__figure">
          <img
            alt={person_target}
            className="audienceInsights__figureImage"
            src={getBuyerInsightsFigure(gender, age)}
          />
          <div className="audienceInsights__personaLabel">
            <span className="audienceInsights__personaLabelTitle">{labels.targetPersona}</span>
            <p className="audienceInsights__personaName">{person_target}</p>
          </div>
        </div>
        <div className="audienceInsights__insightsBody">
          <header className="audienceInsights__insightsHeader">
            <p className="audienceInsights__insightsHeaderTitle">{labels.header}</p>
            <p className="audienceInsights__insightsHeaderSubtitle">{labels.subHeader}</p>
          </header>
          <div className="audienceInsights__demographics">
            <DemographicItem label={labels.genderLabel}>
              <strong>{genderLabel}</strong>
            </DemographicItem>
            <div className="audienceInsights__demographicSeparator" />
            <DemographicItem label={labels.avgAge}>
              <strong>{ageTo ? `${ageFrom} - ${ageTo}` : ageFrom}</strong>
              {` ${labels.yearsOld}`}
            </DemographicItem>
            <div className="audienceInsights__demographicSeparator" />
            <DemographicItem label={labels.statusLabel}>
              <strong>{visitorType}</strong>
            </DemographicItem>
          </div>
          <div className="audienceInsights__body">
            <div className="audienceInsights__interests">
              <h4 className="audienceInsights__sectionTitle">
                <i className="fa-regular fa-circle-check audienceInsights__sectionIcon--green" />
                {labels.interests}
              </h4>
              <ul className="audienceInsights__interestList">
                {iab_cat.map((label, index) => (
                  <li className="audienceInsights__interestItem" key={label}>
                    <span className="audienceInsights__interestLabel">{label}</span>
                    {index === 0 && (
                      <Tag id={`${tagIdPrefix}-${label}`} status="ok">
                        {labels.highAffinity}
                      </Tag>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="audienceInsights__primaryValues">
              <h4 className="audienceInsights__sectionTitle audienceInsights__sectionTitle--accent">
                {labels.primaryValues}
              </h4>
              <ul className="audienceInsights__primaryValueList">
                {primary_values.map(value => (
                  <li className="audienceInsights__primaryValueItem" key={value}>
                    {value}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="audienceInsights__coreNeeds">
            <h4 className="audienceInsights__sectionTitle audienceInsights__sectionTitle--accent">
              {labels.coreNeeds}
            </h4>
            <p className="audienceInsights__coreNeedsText">{core_needs}</p>
          </div>
        </div>
      </div>
      <div className="audienceInsights__affinitiesContent">
        <header className="audienceInsights__affinitiesHeader">
          <p className="audienceInsights__affinitiesHeaderTitle">{labels.affinitiesHeader}</p>
        </header>
        <BrandSection brands={top_brands} title={labels.topBrands} />
        <div className="audienceInsights__affinitiesSeparator" />
        {top_categories || !bottom_brands ? (
          <BrandSection
            brands={(top_categories ?? []).map(name => ({ icon: getPoiCategoryIcon(name), logoS3: null, name }))}
            title={labels.topCategories}
          />
        ) : (
          <BrandSection brands={bottom_brands} title={labels.bottomBrands} />
        )}
      </div>
    </div>
  );
}
